import React from 'react'
import { useSelector } from 'react-redux'
import { Map } from 'immutable'
import TooltipV3, { PLACEMENT_BOTTOM, PLACEMENT_LEFT, PLACEMENT_RIGHT } from '../Tooltip.v3/index'
import {
  historyRedirect,
  HISTORY_METHOD_REPLACE,
} from '../../services/navigation/index'
import { selectAuth } from '../../services/auth/index'
import { selectLocation } from '../../services/resolver/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import ButtonSort from './ButtonSort'

export const SORT_ORDER_ASC = 'ASC'
export const SORT_ORDER_DESC = 'DESC'

/**
 * Sorted Header Cell for Common Table component.
 * @param {Object} props
 * @param {String} props.title // Title for header cell
 * @param {Map} props.column // Column for table
 * @param {Map} props.history // Application history
 * @param {Function} props.historyCallback // Callback function after history change
 * @returns {JSX.Element}
 */
const SortedHeaderCell = ({
  column,
  history,
  historyCallback,
  children,
}) => {
  const auth = useSelector(selectAuth)
  const location = useSelector(selectLocation)
  const language = useSelector(selectPrimaryLanguage)
  const { query } = location
  const {
    sortDirection = SORT_ORDER_ASC,
    sortField,
  } = query
  const fieldKey = column.sortKey || column.key

  const handleSort = () => {
    const newSortDirection = sortDirection === SORT_ORDER_ASC
      ? SORT_ORDER_DESC
      : SORT_ORDER_ASC

    query.sortDirection = newSortDirection
    query.sortField = fieldKey
    query.p = 1

    historyRedirect({
      history,
      url: location.pathname,
      query,
      auth,
      language,
      HISTORY_METHOD_REPLACE,
    }, historyCallback)
  }

  return (
    <ButtonSort
      sortOrder={sortField === fieldKey ? sortDirection : null}
      onClick={handleSort}
    >
      {children}
    </ButtonSort>
  )
}

/**
 * Header Cell for Common Table component.
 * @param {Object} props
 * @param {Map} props.column // Column for table
 * @param {Map} props.history // Application history
 * @param {Function} props.historyCallback // Callback function after history change
 * @param {Boolean} props.isFirst // Is first column
 * @param {Boolean} props.isLast // Is last column
 * @returns {JSX.Element}
 */
const HeaderCell = ({
  column,
  history,
  historyCallback,
  isFirst,
  isLast,
}) => {
  const { title, sorter, tooltipTitle } = column

  let tooltipPlacement = PLACEMENT_BOTTOM

  if (isFirst) {
    tooltipPlacement = PLACEMENT_RIGHT
  }

  if (isLast) {
    tooltipPlacement = PLACEMENT_LEFT
  }

  if (sorter) {
    return (
      <th className="table-header-cell">
        <SortedHeaderCell
          column={column}
          history={history}
          historyCallback={historyCallback}
        >
          <TooltipV3
            title={tooltipTitle}
            placement={tooltipPlacement}
          >
            {title}
          </TooltipV3>
        </SortedHeaderCell>
      </th>
    )
  }

  return (
    <th className="table-header-cell">
      <TooltipV3
        title={tooltipTitle}
        placement={tooltipPlacement}
      >
        {title}
      </TooltipV3>
    </th>
  )
}

const TableHeader = (props) => {
  const { columns } = props
  const lastIndex = columns.length - 1

  return (
    <thead className="table-header">
      <tr>
        {columns.map((column, index) => (
          <HeaderCell
            key={`${column.key}-${index}`}
            column={column}
            isFirst={index === 0}
            isLast={index === lastIndex}
            {...props}
          />
        ))}
      </tr>
    </thead>
  )
}

/**
 * Row for rendering table totals values, summary etc.
 * @param {Object} props
 * @param {Map} props.subHeaderData // Map data for total row
 * @param {String[]} props.fieldsKey // Array of field key name
 * @param {List} props.columns // List of columns for table
 * @returns {JSX.Element}
 */
const TableTotalRow = ({ totalRowData, fieldsKey, columns }) => {
  if (!totalRowData) {
    return null
  }

  return (
    <tr className="table-row table-row__bold">
      {fieldsKey.map((key, index) => {
        const currentColumn = columns[index] || {}
        const { render } = currentColumn

        const value = totalRowData.get(key, '')

        if (!value) {
          return (
            <td className="table-cell" key={`${key}-${index}`}>
              <span>&mdash;</span>
            </td>
          )
        }

        return (
          <td className="table-cell" key={`${key}-${index}`}>
            {render
              ? render(value, totalRowData, -1)
              : `${value}`
            }
          </td>
        )
      })}
    </tr>
  )
}

const TableBodyContent = (props) => {
  const {
    fieldsKey,
    columns,
    item,
    tableDataIndex,
  } = props

  return fieldsKey.map((key, index) => {
    const currentColumn = columns[index] || {}
    const { render, accessor } = currentColumn
    const keyItem = item.get(key) || Map()

    let cellValue = Map.isMap(keyItem) ? '-' : keyItem

    if (accessor) {
      cellValue = accessor
        .split('.')
        .reduce((acc, accessorKey) => {
          if (Map.isMap(acc)) {
            return acc.get(accessorKey)
          }

          return '-'
        }, item.get(key, Map()))
    }

    return (
      <td className="table-cell" key={`${tableDataIndex}-${key}`}>
        {render
          // render callback has:
          // field value, the whole item (row) values and index in the table
          ? render(cellValue, item, tableDataIndex)
          : cellValue
        }
      </td>
    )
  })
}

/**
 * Table Body for Common Table component.
 * @param {Object} props
 * @param {List} props.tableData // List data for table
 * @param {List} props.columns // List of columns for table
 * @param {String[]} props.fieldsKey // Array of field key name
 * @param {String} props.rowKey // Key for table row
 * @returns {JSX.Element}
 */
const TableBody = ({
  tableData,
  columns,
  fieldsKey,
  rowKey,
}) => {
  return tableData.map((item, index) => (
    <tr className="table-row" key={item.get(rowKey) || index}>
      <TableBodyContent
        fieldsKey={fieldsKey}
        columns={columns}
        item={item}
        tableDataIndex={index}
      />
    </tr>
  ))
}

/**
 * Mobile Table row component.
 * @param {Object} props
 * @param {String[]} props.fieldsKey // Array of field key name
 * @param {List} props.columns // List of columns for table
 * @param {Map} props.tableDataItem // List data for table
 * @param {Number} props.tableDataIndex // index of tableData
 * @returns {JSX.Element}
 */
const MobileTableRow = ({
  fieldsKey,
  columns,
  tableDataItem,
  tableDataIndex = -1,
}) => fieldsKey.map((keyName, keyIndex) => {
  const currentColumn = columns[keyIndex] || {}
  const { render, title } = currentColumn

  const value = tableDataItem.get(keyName, '')

  return (
    <div
      key={`${keyName}-${keyIndex}`}
      className="table-container__mobile-cell"
    >
      <div className="table-container__mobile-label">
        {title}
      </div>
      <div className="table-container__mobile-value">
        {render
          ? render(value, tableDataItem, tableDataIndex)
          : `${value}`
        }
      </div>
    </div>
  )
})

/**
 * Mobile Table view component.
 * @param {Object} props
 * @param {List} props.columns // List of columns for table
 * @param {String[]} props.fieldsKey // Array of field key name
 * @param {List} props.tableData // List data for table
 * @param {String} props.rowKey // Key for table row
 * @returns {JSX.Element}
 */
const MobileTableView = ({
  columns, fieldsKey, tableData, rowKey,
}) => {
  if (!tableData || !tableData.size) {
    return <></>
  }

  return tableData.map((tableDataItem, tableDataIndex) => {
    return (
      <div
        className="table-container__mobile-row"
        key={tableDataItem.get(rowKey) || tableDataIndex}
      >
        <MobileTableRow
          fieldsKey={fieldsKey}
          columns={columns}
          tableDataItem={tableDataItem}
          tableDataIndex={tableDataIndex}
        />
      </div>
    )
  })
}

/**
 * Common Table component.
 * @param {Object} props
 * @param {List} props.columns // List of columns for table
 * @param {List} props.tableData // List data for table
 * @param {Map} props.subHeaderData // Map data for sub header
 * @param {Map} props.summaryHeaderData // Map data for summary header
 * @param {Map} props.history // Application history
 * @param {String} props.rowKey // Key for table row
 * @param {Function} props.historyCallback // Callback function after history change
 * @param {Boolean} props.loading // Is table data loading
 * @returns {JSX.Element}
 */
const Table = ({
  columns,
  tableData,
  subHeaderData,
  summaryRowData,
  history,
  rowKey = 'id',
  historyCallback,
  loading = false,
}) => {
  if (loading) {
    return (
      <>
        <div className="table-container__placeholder-row" />
        <div className="table-container__placeholder-row" />
        <div className="table-container__placeholder-row" />
        <div className="table-container__placeholder-row" />
      </>
    )
  }

  const fieldsKey = columns.map(({ key }) => key)

  return (
    <div className="table-container">
      <div className="table-container__desktop-container">
        <table className="table">
          <TableHeader
            columns={columns}
            history={history}
            historyCallback={historyCallback}
            subHeaderData={subHeaderData}
            fieldsKey={fieldsKey}
          />
          <tbody>
            <TableTotalRow
              columns={columns}
              totalRowData={subHeaderData}
              fieldsKey={fieldsKey}
            />
            <TableBody
              tableData={tableData}
              columns={columns}
              fieldsKey={fieldsKey}
              rowKey={rowKey}
            />
            <TableTotalRow
              columns={columns}
              totalRowData={summaryRowData}
              fieldsKey={fieldsKey}
            />
          </tbody>
        </table>
      </div>
      <div className="table-container__mobile-container">
        <MobileTableView
          columns={columns}
          tableData={tableData}
          fieldsKey={fieldsKey}
          rowKey={rowKey}
        />
      </div>
    </div>
  )
}

export default Table
